// マスター編集画面（ドライバー一覧 + 追加・編集・削除）
//
// 設計メモ:
// - 【シート連携中（Phase 1〜2）】正本＝Googleスプレッドシート。
//     追加 → /api/master/add（管理番号はサーバ採番）
//     削除 → /api/master/retire（行は消さず契約終了日を入れる論理削除）
//     編集 → アプリからは不可。シートで直接編集 →「再読込」で反映（上書き同期はしない方針 R5）
// - 【未接続時（フォールバック）】masters.json + driverOverrides（localStorage）の従来動作
// - 「未登録から追加」導線：StatementList の警告チップから初期氏名つきで遷移してくる

import React, { useMemo, useState, useEffect } from "react";
import { Ic } from "../components/Icons";
import { useStore, nextDriverId } from "../store";
import { normalizeName } from "../lib/normalize";
import { addDriverToSheet, retireDriverInSheet, reactivateDriverInSheet } from "../lib/masterApi";
import { isRetiredDriver, hasStatusConflict, isScheduledRetirement } from "../lib/driverStatus";
import type { Driver } from "../types";

export type MasterEditInitial = {
  // 一覧画面からの「未登録ドライバーを追加」導線で渡される初期値
  prefillName?: string;
};

type SortKey =
  | "id"
  | "name"
  | "org"
  | "branch"
  | "unitTakkyu"
  | "unitNekopos"
  | "unitDm"
  | "invoiceNo"
  | "contractFrom";
type SortDir = "asc" | "desc";

export function MasterEditScreen({ initial }: { initial?: MasterEditInitial }) {
  const { state, dispatch, refreshMaster, masterLoading, masterError } = useStore();
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Driver | null>(null);
  const [adding, setAdding] = useState<Partial<Driver> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Driver | null>(null);
  const [confirmReactivate, setConfirmReactivate] = useState<Driver | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("id");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [statusFilter, setStatusFilter] = useState<"active" | "retired" | "all">("active");
  const [busy, setBusy] = useState(false);

  // 退職予定（未来日の契約終了日）と消し忘れ（過去日）を分けるための基準日
  const todayIso = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  }, []);

  // シート連携中か（true ならスプレッドシートが正本）
  const sheetMode = !!state.sheetMaster;
  const sheetMaster = state.sheetMaster;

  // 一覧画面から「未登録の氏名」を渡されたら、フォームを開いた状態で起動する
  useEffect(() => {
    if (initial?.prefillName) {
      setAdding({ name: initial.prefillName });
    }
    // initial は呼び出し元で一度きり渡される想定
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.prefillName]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim();
    const qn = normalizeName(q);
    let base = !q
      ? state.drivers
      : state.drivers.filter(
          (d) =>
            d.id.includes(q) ||
            d.name.includes(q) ||
            d.nameNormalized.includes(qn) ||
            (d.branch && d.branch.includes(q)) ||
            (d.org && d.org.includes(q))
        );
    if (statusFilter !== "all") {
      base = base.filter((d) => {
        const isRetired = isRetiredDriver(d);
        return statusFilter === "retired" ? isRetired : !isRetired;
      });
    }
    return [...base].sort(compareDrivers(sortKey, sortDir));
  }, [state.drivers, query, sortKey, sortDir, statusFilter]);

  const overrides = state.driverOverrides;
  const addedCount = overrides.added.length;
  const updatedCount = Object.keys(overrides.updated).length;
  const deletedCount = overrides.deletedIds.length;
  const hasOverrides = addedCount + updatedCount + deletedCount > 0;

  const addedIds = useMemo(() => new Set(overrides.added.map((a) => a.id)), [overrides.added]);
  const updatedIds = useMemo(() => new Set(Object.keys(overrides.updated)), [overrides.updated]);

  const handleSave = async (d: Driver, mode: "add" | "edit") => {
    if (sheetMode) {
      // シート連携中: 追加はシートへ書き込み（編集はUIで塞いでいる）
      if (mode !== "add") return;
      setBusy(true);
      try {
        const r = await addDriverToSheet({
          name: d.name,
          org: d.org,
          branch: d.branch,
          email: d.email || undefined,
          contractFrom: d.contractFrom || undefined,
          unitTakkyu: d.unitTakkyu,
          unitNekopos: d.unitNekopos,
          unitDm: d.unitDm,
          invoiceNo: d.invoiceNo,
          monthlyKeitai: d.monthlyKeitai,
          monthlySharyo: d.monthlySharyo,
          hasGasCard: d.hasGasCard,
          monthlyAppli: d.monthlyAppli,
          shidoCount: d.shidoCount,
        });
        if (!r.ok) {
          alert(`マスターシートへの追加に失敗しました:\n${r.error || "不明なエラー"}`);
          return;
        }
        await refreshMaster();
        setAdding(null);
        // 一部だけ失敗したとき（月別控除入力シートへの行追加や AT-STYLE 列なし）は
        // サーバが warnings で返す。ここで出さないと「追加成功」としか見えず、
        // 月別控除入力に名前が無いまま気づけない（Codex レビュー 2026-09-14 P1）。
        const ws = r.warnings ?? [];
        const head = `マスターシートに追加しました（管理番号 ${r.empId}）`;
        const detail = ws.map((w) => "・" + w).join("\n");
        alert(ws.length > 0 ? head + "\n\n⚠️ ただし次の点をご確認ください:\n" + detail : head);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (mode === "add") {
      dispatch({ type: "ADD_DRIVER", driver: d });
      setAdding(null);
    } else {
      dispatch({ type: "UPDATE_DRIVER", driver: d });
      setEditing(null);
    }
  };

  const handleDelete = (d: Driver) => {
    dispatch({ type: "DELETE_DRIVER", driverId: d.id });
    setConfirmDelete(null);
  };

  // シート連携中の「削除」＝退職処理（契約終了日を書き込む論理削除）
  const handleRetire = async (d: Driver, contractTo: string) => {
    setBusy(true);
    try {
      const r = await retireDriverInSheet(d.id, contractTo);
      if (!r.ok) {
        alert(`退職処理に失敗しました:\n${r.error || "不明なエラー"}`);
        return;
      }
      await refreshMaster();
      setConfirmDelete(null);
    } finally {
      setBusy(false);
    }
  };

  // 再稼働＝退職処理の取り消し（契約終了日を空にし、状態が「退職」なら「在職」に戻す）
  const handleReactivate = async (d: Driver) => {
    setBusy(true);
    try {
      const r = await reactivateDriverInSheet(d.id);
      if (!r.ok) {
        alert(`再稼働の処理に失敗しました:\n${r.error || "不明なエラー"}`);
        return;
      }
      await refreshMaster();
      setConfirmReactivate(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="content" style={{ maxWidth: 1280 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">マスター編集</div>
          <div className="page-sub muted">
            ドライバー {state.drivers.length} 名
            {sheetMode && sheetMaster ? (
              <>
                {" "}／{" "}
                <span style={{ color: "#2db15a", fontWeight: 600 }}>
                  シート連携中
                </span>{" "}
                <span className="tiny">
                  （{new Date(sheetMaster.fetchedAt).toLocaleString("ja-JP")} 取得）
                </span>
              </>
            ) : (
              <>
                {" "}／ <span style={{ color: "#b8860b" }}>ローカルデータ（シート未接続）</span>
                {hasOverrides && (
                  <>
                    {" "}／{" "}
                    <span style={{ color: "#2b51c2" }}>
                      追加 {addedCount} / 編集 {updatedCount} / 削除 {deletedCount}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {sheetMode && sheetMaster && (
          <a
            className="btn ghost"
            href={sheetMaster.sheetUrl}
            target="_blank"
            rel="noreferrer"
            title="正本のスプレッドシートを開く（単価などの変更はシートで直接編集）"
          >
            シートを開く
          </a>
        )}
        <button
          className="btn ghost"
          onClick={() => void refreshMaster()}
          disabled={masterLoading}
          title="マスターシートから最新を再取得"
        >
          <Ic.history size={14} /> {masterLoading ? "取得中..." : "再読込"}
        </button>
        <button
          className="btn primary"
          onClick={() => setAdding({})}
          title="新規ドライバーを追加"
          disabled={busy}
        >
          <Ic.plus size={14} /> 新規追加
        </button>
      </div>

      {masterError && (
        <div className="toast warn" style={{ marginBottom: 12 }}>
          <Ic.alert size={14} /> マスターシートの取得に失敗（前回取得分で動作中）: {masterError}
        </div>
      )}
      {sheetMode && sheetMaster && sheetMaster.warnings.length > 0 && (
        <div className="toast warn" style={{ marginBottom: 12 }}>
          <Ic.alert size={14} />
          <div className="col gap-4">
            {sheetMaster.warnings.map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head row gap-12" style={{ alignItems: "center" }}>
          <div className="search-wrap" style={{ flex: 1, maxWidth: 360 }}>
            <Ic.search size={14} />
            <input
              className="input search"
              placeholder="氏名 / ID / 所属 / 支店で検索"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="row gap-4">
            {(["active", "retired", "all"] as const).map((f) => (
              <button
                key={f}
                className={"btn sm" + (statusFilter === f ? " primary" : " ghost")}
                onClick={() => setStatusFilter(f)}
              >
                {f === "active" ? "在籍" : f === "retired" ? "退職" : "全員"}
              </button>
            ))}
          </div>
          <span className="tiny muted">{filtered.length} 件 表示中</span>
        </div>
        <div className="tbl-scroll" style={{ maxHeight: "70vh" }}>
          <table className="tbl">
            <thead>
              <tr>
                <SortableTh sortKey="id" current={sortKey} dir={sortDir} onSort={handleSort} style={{ width: 80 }}>ID</SortableTh>
                <SortableTh sortKey="name" current={sortKey} dir={sortDir} onSort={handleSort}>氏名</SortableTh>
                <SortableTh sortKey="org" current={sortKey} dir={sortDir} onSort={handleSort}>所属</SortableTh>
                <SortableTh sortKey="branch" current={sortKey} dir={sortDir} onSort={handleSort}>支店</SortableTh>
                <SortableTh sortKey="unitTakkyu" current={sortKey} dir={sortDir} onSort={handleSort} className="num">宅急便</SortableTh>
                <SortableTh sortKey="unitNekopos" current={sortKey} dir={sortDir} onSort={handleSort} className="num">ネコポス</SortableTh>
                <SortableTh sortKey="unitDm" current={sortKey} dir={sortDir} onSort={handleSort} className="num">DM</SortableTh>
                <SortableTh sortKey="invoiceNo" current={sortKey} dir={sortDir} onSort={handleSort}>インボイス</SortableTh>
                <SortableTh sortKey="contractFrom" current={sortKey} dir={sortDir} onSort={handleSort}>契約期間</SortableTh>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const tag = addedIds.has(d.id)
                  ? { label: "追加", color: "#2db15a" }
                  : updatedIds.has(d.id)
                  ? { label: "編集", color: "#2b51c2" }
                  : null;
                const retired = isRetiredDriver(d);
                // 未来日の契約終了日は「退職予定」＝正常なデータなので、警告にも再稼働対象にもしない
                const scheduled = isScheduledRetirement(d, todayIso);
                const statusConflict = hasStatusConflict(d, todayIso);
                return (
                  // 退職行は薄く見せるが、opacity を使うと子要素側で打ち消せず
                  // 操作ボタン（再稼働）まで無効に見えてしまう。文字色だけ落とす。
                  <tr key={d.id} style={retired ? { color: "#98a2b3" } : undefined}>
                    <td className="mono muted">{d.id}</td>
                    <td>
                      <div className="row gap-8" style={{ alignItems: "center" }}>
                        <span>{d.name}</span>
                        {d.verifiedEmail && (
                          <span
                            className="tiny"
                            style={{ padding: "1px 6px", borderRadius: 4, background: "#2db15a22", color: "#2db15a", fontWeight: 600 }}
                            title={`認証済メール: ${d.verifiedEmail}`}
                          >
                            ✓認証済
                          </span>
                        )}
                        {retired && (
                          <span
                            className="tiny"
                            style={{
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#88888822",
                              color: "#777",
                              fontWeight: 600,
                            }}
                          >
                            退職
                          </span>
                        )}
                        {scheduled && (
                          <span
                            className="tiny"
                            style={{
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#2b51c222",
                              color: "#2b51c2",
                              fontWeight: 600,
                            }}
                            title="状態は在職のまま、契約終了日が今日以降に入っています（退職予定）"
                          >
                            {d.contractTo} 契約終了予定
                          </span>
                        )}
                        {statusConflict && (
                          <span
                            className="tiny"
                            style={{
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#f2c94c22",
                              color: "#a67c00",
                              fontWeight: 600,
                            }}
                            title="状態列は在職ですが、過ぎた契約終了日が残っています（消し忘れの可能性）"
                          >
                            ⚠ 契約終了日が残っています
                          </span>
                        )}
                        {tag && (
                          <span
                            className="tiny"
                            style={{
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: tag.color + "22",
                              color: tag.color,
                              fontWeight: 600,
                            }}
                          >
                            {tag.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{d.org}</td>
                    <td>{d.branch}</td>
                    <td className="num">{d.unitTakkyu ?? "—"}</td>
                    <td className="num">{d.unitNekopos ?? "—"}</td>
                    <td className="num">{d.unitDm ?? "—"}</td>
                    <td className="tiny mono">
                      {d.invoiceNo ?? <span className="muted">未登録</span>}
                    </td>
                    <td className="tiny">
                      {d.contractFrom ?? "—"} 〜{" "}
                      {d.contractTo ?? <span className="muted">在籍中</span>}
                    </td>
                    <td>
                      <div className="row gap-4">
                        {sheetMode && (retired || statusConflict) && (
                          // 隣の鉛筆が常時グレーアウト（シート連携中は編集不可）なので、
                          // ghost のままだと「押せないボタン」に見える。枠線と背景で押せることを示す。
                          <button
                            className="btn sm"
                            title={
                              statusConflict
                                ? "再稼働のため、残っている契約終了日を消します"
                                : "再稼働（契約終了日を消して在籍に戻す）"
                            }
                            style={{
                              color: "#fff",
                              background: "#2db15a",
                              borderColor: "#2db15a",
                              fontWeight: 700,
                              opacity: 1,
                            }}
                            disabled={busy}
                            onClick={() => setConfirmReactivate(d)}
                          >
                            <Ic.history size={13} /> 再稼働
                          </button>
                        )}
                        <button
                          className="btn sm ghost"
                          title={
                            sheetMode
                              ? "シート連携中は、単価などの変更はマスターシートで直接編集 →「再読込」してください"
                              : "編集"
                          }
                          disabled={sheetMode}
                          style={sheetMode ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
                          onClick={() => !sheetMode && setEditing(d)}
                        >
                          <Ic.edit size={13} />
                        </button>
                        {statusConflict ? (
                          // 契約終了日を過ぎているのに状態が在職のまま＝退職処理が完了していない。
                          // 放置すると支払対象・メアド収集対象に残り続けるため、完了させる導線を出す。
                          <button
                            className="btn sm"
                            title="契約終了日を過ぎています。状態を「退職」にして退職処理を完了します"
                            style={{
                              color: "#fff",
                              background: "#e64646",
                              borderColor: "#e64646",
                              fontWeight: 700,
                              opacity: 1,
                            }}
                            disabled={busy}
                            onClick={() => setConfirmDelete(d)}
                          >
                            <Ic.trash size={13} /> 退職を完了
                          </button>
                        ) : (
                          <button
                            className="btn sm ghost"
                            title={sheetMode ? "退職処理（契約終了日を入れる）" : "削除"}
                            style={{ color: "#e64646" }}
                            disabled={busy || (sheetMode && retired)}
                            onClick={() => setConfirmDelete(d)}
                          >
                            <Ic.trash size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted" style={{ textAlign: "center", padding: 24 }}>
                    該当するドライバーがありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || adding) && (
        <DriverFormModal
          initial={editing ?? adding ?? {}}
          mode={editing ? "edit" : "add"}
          sheetMode={sheetMode}
          busy={busy}
          existingIds={new Set(state.drivers.map((d) => d.id))}
          onSave={handleSave}
          onClose={() => {
            setEditing(null);
            setAdding(null);
          }}
          suggestNextId={() => nextDriverId(state.drivers)}
        />
      )}

      {confirmDelete && sheetMode && (
        <RetireModal
          driver={confirmDelete}
          busy={busy}
          onConfirm={(date) => void handleRetire(confirmDelete, date)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
      {confirmDelete && !sheetMode && (
        <ConfirmDeleteModal
          driver={confirmDelete}
          onConfirm={() => handleDelete(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
      {confirmReactivate && sheetMode && (
        <ReactivateModal
          driver={confirmReactivate}
          busy={busy}
          onConfirm={() => void handleReactivate(confirmReactivate)}
          onClose={() => setConfirmReactivate(null)}
        />
      )}
    </div>
  );
}

// ===== 編集・追加共通モーダル =====

const INVOICE_RE = /^T\d{13}$/;

function DriverFormModal({
  initial,
  mode,
  sheetMode,
  busy,
  existingIds,
  onSave,
  onClose,
  suggestNextId,
}: {
  initial: Partial<Driver>;
  mode: "add" | "edit";
  sheetMode: boolean;
  busy: boolean;
  existingIds: Set<string>;
  onSave: (d: Driver, mode: "add" | "edit") => void;
  onClose: () => void;
  suggestNextId: () => string;
}) {
  const [form, setForm] = useState<Partial<Driver>>(() => {
    if (mode === "add") {
      return {
        id: initial.id || (sheetMode ? "" : suggestNextId()),
        name: initial.name || "",
        org: initial.org || "個人",
        branch: initial.branch || "",
        contractFrom: initial.contractFrom || "",
        contractTo: initial.contractTo || null,
        unitTakkyu: initial.unitTakkyu ?? null,
        unitNekopos: initial.unitNekopos ?? null,
        unitDm: initial.unitDm ?? null,
        invoiceNo: initial.invoiceNo ?? null,
        contractDocOk: initial.contractDocOk ?? false,
        monthlyKeitai: initial.monthlyKeitai ?? null,
        monthlySharyo: initial.monthlySharyo ?? null,
        hasGasCard: initial.hasGasCard ?? false,
        gasUsage: initial.gasUsage ?? null,
        monthlyAppli: initial.monthlyAppli ?? null,
        shidoCount: initial.shidoCount ?? null,
      };
    }
    return { ...initial };
  });
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof Driver>(key: K, value: Driver[K] | null) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const setNum = (key: keyof Driver, raw: string) => {
    if (raw.trim() === "") {
      set(key, null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    set(key, n as never);
  };

  const handleSubmit = () => {
    const name = (form.name || "").trim();
    const id = (form.id || "").trim();
    if (!sheetMode || mode === "edit") {
      // シート連携の新規追加はサーバ側で管理番号を採番するため ID 入力は不要
      if (!id) {
        setError("ID を入力してください");
        return;
      }
      if (mode === "add" && existingIds.has(id)) {
        setError(`ID "${id}" は既に使用されています`);
        return;
      }
    }
    if (!name) {
      setError("氏名を入力してください");
      return;
    }
    if (form.invoiceNo && !INVOICE_RE.test(form.invoiceNo)) {
      setError("インボイス番号は T + 13桁の形式で入力してください（例: T1234567890123）");
      return;
    }
    const driver: Driver = {
      id,
      name,
      nameNormalized: normalizeName(name),
      org: form.org || "",
      branch: form.branch || "",
      email: emptyToNull(form.email ?? null),
      contractFrom: emptyToNull(form.contractFrom),
      contractTo: emptyToNull(form.contractTo),
      unitTakkyu: form.unitTakkyu ?? null,
      unitNekopos: form.unitNekopos ?? null,
      unitDm: form.unitDm ?? null,
      invoiceNo: emptyToNull(form.invoiceNo),
      contractDocOk: !!form.contractDocOk,
      monthlyKeitai: form.monthlyKeitai ?? null,
      monthlySharyo: form.monthlySharyo ?? null,
      hasGasCard: !!form.hasGasCard,
      gasUsage: form.gasUsage ?? null,
      monthlyAppli: form.monthlyAppli ?? null,
      shidoCount: form.shidoCount ?? null,
    };
    onSave(driver, mode);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-head">
          <div className="card-title">{mode === "add" ? "ドライバーを追加" : "ドライバーを編集"}</div>
          <button className="btn btn-icon-only ghost" onClick={onClose}>
            <Ic.x size={16} />
          </button>
        </div>
        <div className="modal-body col gap-12">
          {error && (
            <div className="toast warn">
              <Ic.alert size={14} /> {error}
            </div>
          )}

          <Section title="基本情報">
            <Row>
              <Field label={sheetMode && mode === "add" ? "管理番号（自動採番）" : "ID"} required={!(sheetMode && mode === "add")}>
                <input
                  className="input"
                  value={sheetMode && mode === "add" ? "保存時に自動採番されます" : form.id || ""}
                  onChange={(e) => set("id", e.target.value)}
                  disabled={mode === "edit" || (sheetMode && mode === "add")}
                  style={{ fontFamily: "monospace" }}
                />
              </Field>
              <Field label="氏名" required>
                <input
                  className="input"
                  value={form.name || ""}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="例: 佐藤　義継"
                />
              </Field>
            </Row>
            <Row>
              <Field label="所属">
                <input
                  className="input"
                  value={form.org || ""}
                  onChange={(e) => set("org", e.target.value)}
                  placeholder="個人 / 法人名"
                />
              </Field>
              <Field label="支店">
                <input
                  className="input"
                  value={form.branch || ""}
                  onChange={(e) => set("branch", e.target.value)}
                  placeholder="例: 佐川岩国 / ヤマト広島ベース"
                />
              </Field>
              <Field label="メールアドレス（承認通知先・任意）">
                <input
                  className="input"
                  type="email"
                  value={form.email || ""}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="例: taro@example.com"
                />
              </Field>
            </Row>
            <Row>
              <Field label="契約開始日">
                <input
                  type="date"
                  className="input"
                  value={form.contractFrom || ""}
                  onChange={(e) => set("contractFrom", e.target.value)}
                />
              </Field>
              <Field label="契約終了日（在籍中は空欄）">
                <input
                  type="date"
                  className="input"
                  value={form.contractTo || ""}
                  onChange={(e) => set("contractTo", e.target.value || null)}
                />
              </Field>
            </Row>
          </Section>

          <Section title="単価">
            <Row>
              <Field label="宅急便（円/個）">
                <input
                  className="input"
                  type="number"
                  value={form.unitTakkyu ?? ""}
                  onChange={(e) => setNum("unitTakkyu", e.target.value)}
                />
              </Field>
              <Field label="ネコポス（円/個）">
                <input
                  className="input"
                  type="number"
                  value={form.unitNekopos ?? ""}
                  onChange={(e) => setNum("unitNekopos", e.target.value)}
                />
              </Field>
              <Field label="DM（円/個）">
                <input
                  className="input"
                  type="number"
                  value={form.unitDm ?? ""}
                  onChange={(e) => setNum("unitDm", e.target.value)}
                />
              </Field>
            </Row>
          </Section>

          <Section title="月次控除（任意）">
            <Row>
              <Field label="月額携帯代">
                <input
                  className="input"
                  type="number"
                  value={form.monthlyKeitai ?? ""}
                  onChange={(e) => setNum("monthlyKeitai", e.target.value)}
                />
              </Field>
              <Field label="月額車両費">
                <input
                  className="input"
                  type="number"
                  value={form.monthlySharyo ?? ""}
                  onChange={(e) => setNum("monthlySharyo", e.target.value)}
                />
              </Field>
              <Field label="月額アプリ代">
                <input
                  className="input"
                  type="number"
                  value={form.monthlyAppli ?? ""}
                  onChange={(e) => setNum("monthlyAppli", e.target.value)}
                />
              </Field>
            </Row>
            <Row>
              <Field label="指導料 件数">
                <input
                  className="input"
                  type="number"
                  value={form.shidoCount ?? ""}
                  onChange={(e) => setNum("shidoCount", e.target.value)}
                />
              </Field>
              <Field label="ガソリンカード">
                <label className="row gap-8" style={{ alignItems: "center", height: 36 }}>
                  <input
                    type="checkbox"
                    checked={!!form.hasGasCard}
                    onChange={(e) => set("hasGasCard", e.target.checked)}
                  />
                  <span>利用あり</span>
                </label>
              </Field>
              <Field label="ガソリン利用回数">
                <input
                  className="input"
                  type="number"
                  value={form.gasUsage ?? ""}
                  onChange={(e) => setNum("gasUsage", e.target.value)}
                  disabled={!form.hasGasCard}
                />
              </Field>
            </Row>
          </Section>

          <Section title="インボイス・契約書">
            <Row>
              <Field label="インボイス番号（T+13桁）">
                <input
                  className="input"
                  value={form.invoiceNo ?? ""}
                  onChange={(e) => set("invoiceNo", e.target.value)}
                  placeholder="T1234567890123"
                  style={{ fontFamily: "monospace" }}
                />
              </Field>
              <Field label="契約書">
                <label className="row gap-8" style={{ alignItems: "center", height: 36 }}>
                  <input
                    type="checkbox"
                    checked={!!form.contractDocOk}
                    onChange={(e) => set("contractDocOk", e.target.checked)}
                  />
                  <span>受領済</span>
                </label>
              </Field>
            </Row>
          </Section>
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>キャンセル</button>
          <button className="btn primary" onClick={handleSubmit} disabled={busy}>
            <Ic.check size={14} />{" "}
            {busy
              ? "シートに書き込み中..."
              : mode === "add"
              ? sheetMode
                ? "シートに追加する"
                : "追加する"
              : "保存する"}
          </button>
        </div>
      </div>
    </div>
  );
}

// シート連携中の「削除」＝退職処理モーダル（契約終了日を入れる論理削除）
function RetireModal({
  driver,
  busy,
  onConfirm,
  onClose,
}: {
  driver: Driver;
  busy: boolean;
  onConfirm: (contractTo: string) => void;
  onClose: () => void;
}) {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
    today.getDate()
  ).padStart(2, "0")}`;
  // 既に契約終了日が入っている（＝退職予定・または処理途中）ならそれを引き継ぐ
  const [date, setDate] = useState(driver.contractTo || todayIso);
  // 未来日を入れる＝まだ稼働期間が残っている。この場合は状態を「退職」にせず在職のまま残す
  const scheduled = !!date && date > todayIso;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div className="card-title">退職・契約終了の処理</div>
          <button className="btn btn-icon-only ghost" onClick={onClose}>
            <Ic.x size={16} />
          </button>
        </div>
        <div className="modal-body col gap-12">
          <div className="toast" style={{ background: "#f4f6fa" }}>
            <Ic.info size={14} />
            <div>
              <b>{driver.name}</b>（{driver.id}）の行は<b>消さず</b>、マスターシートの
              「契約終了日」に日付を書き込みます（過去の通知書から参照できるよう残します）。
            </div>
          </div>
          <Field label="契約終了日" required>
            <input
              type="date"
              className="input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          {scheduled ? (
            <div className="toast" style={{ background: "#2b51c211" }}>
              <Ic.info size={14} />
              <div className="tiny">
                この日付は<b>まだ先</b>のため、<b>在職のまま「契約終了予定」</b>として登録します。
                それまでは支払通知書の対象・メールアドレス収集の対象に残ります。
                日付を過ぎたら、この画面の「退職を完了」から状態を退職にしてください。
              </div>
            </div>
          ) : (
            <div className="toast" style={{ background: "#f2c94c22" }}>
              <Ic.alert size={14} />
              <div className="tiny">
                状態を<b>「退職」</b>にします。この方は以降、支払通知書の対象・メールアドレス収集の
                対象から外れます。
              </div>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>キャンセル</button>
          <button
            className="btn"
            style={{ background: "#e64646", color: "#fff", borderColor: "#e64646" }}
            disabled={busy || !date}
            onClick={() => onConfirm(date)}
          >
            <Ic.check size={14} />{" "}
            {busy ? "書き込み中..." : scheduled ? "契約終了予定として登録" : "退職にする"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 再稼働モーダル（RetireModal の対称操作）。
// 書き込むセルを明示し、実行前後の値を見せてから確定させる。
function ReactivateModal({
  driver,
  busy,
  onConfirm,
  onClose,
}: {
  driver: Driver;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const willClearContractTo = !!driver.contractTo;
  const willRestoreStatus = driver.status === "退職";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div className="card-title">再稼働の処理</div>
          <button className="btn btn-icon-only ghost" onClick={onClose}>
            <Ic.x size={16} />
          </button>
        </div>
        <div className="modal-body col gap-12">
          <div className="toast" style={{ background: "#f4f6fa" }}>
            <Ic.info size={14} />
            <div>
              <b>{driver.name}</b>（{driver.id}）を<b>再稼働</b>させます。マスターシートの
              <b>下の2つのセルだけ</b>を書き換えます（単価などの他の項目は変更しません）。
            </div>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>項目</th>
                <th>現在</th>
                <th style={{ width: 130 }}>処理後</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>契約終了日</td>
                <td className="tiny">{driver.contractTo || <span className="muted">（空）</span>}</td>
                <td className="tiny">
                  {willClearContractTo ? (
                    <b style={{ color: "#2db15a" }}>（空にする）</b>
                  ) : (
                    <span className="muted">変更なし</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>状態</td>
                <td className="tiny">{driver.status || <span className="muted">（未設定）</span>}</td>
                <td className="tiny">
                  {willRestoreStatus ? (
                    // 実シートは「在籍」「在職」が混在しているため、書き戻す表記は
                    // サーバ側でシートの多数派に合わせる。ここでは具体名を断定しない
                    <b style={{ color: "#2db15a" }}>在籍中に戻す</b>
                  ) : (
                    <span className="muted">変更なし</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="tiny muted" style={{ lineHeight: 1.7 }}>
            再稼働すると、この方は当月以降の支払通知書の対象に戻り、公開フォームからのメールアドレス
            登録もできるようになります。（誤って処理した場合は、ゴミ箱アイコンから改めて契約終了日を
            入れ直せます）
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>
            キャンセル
          </button>
          <button
            className="btn"
            style={{ background: "#2db15a", color: "#fff", borderColor: "#2db15a" }}
            disabled={busy}
            onClick={onConfirm}
          >
            <Ic.check size={14} /> {busy ? "書き込み中..." : "再稼働にする"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  driver,
  onConfirm,
  onClose,
}: {
  driver: Driver;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <div className="card-title">ドライバーを削除</div>
          <button className="btn btn-icon-only ghost" onClick={onClose}>
            <Ic.x size={16} />
          </button>
        </div>
        <div className="modal-body col gap-12">
          <div className="toast warn">
            <Ic.alert size={14} />
            <div>
              <b>{driver.name}</b>（{driver.id}）をマスターから削除します。
              <div className="tiny" style={{ marginTop: 4 }}>
                取込済のCSVに該当行があっても、計算結果から外れます。
              </div>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>キャンセル</button>
          <button
            className="btn"
            style={{ background: "#e64646", color: "#fff", borderColor: "#e64646" }}
            onClick={onConfirm}
          >
            <Ic.trash size={14} /> 削除する
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== 小さなレイアウト部品 =====

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="col gap-8">
      <div className="tiny muted" style={{ fontWeight: 600, letterSpacing: "0.02em" }}>
        {title}
      </div>
      <div className="col gap-8">{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="row gap-12" style={{ flexWrap: "wrap" }}>
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="col gap-4 driver-form-field"
      style={{ flex: "1 1 200px", minWidth: 180 }}
    >
      <label className="tiny muted">
        {label}
        {required && <span style={{ color: "#e64646", marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function emptyToNull(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  return t === "" ? null : t;
}

// ===== ソート =====

function SortableTh({
  sortKey,
  current,
  dir,
  onSort,
  children,
  className,
  style,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  const active = current === sortKey;
  const arrow = active ? (dir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      className={className}
      style={{ ...style, cursor: "pointer", userSelect: "none" }}
      onClick={() => onSort(sortKey)}
      title={active ? `${dir === "asc" ? "昇順" : "降順"}（クリックで反転）` : "クリックでソート"}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {children}
        <span style={{ fontSize: 9, color: active ? "#2b51c2" : "#cbd0d8", width: 9 }}>
          {arrow || "↕"}
        </span>
      </span>
    </th>
  );
}

function compareDrivers(key: SortKey, dir: SortDir): (a: Driver, b: Driver) => number {
  const mul = dir === "asc" ? 1 : -1;
  return (a, b) => {
    const av = a[key];
    const bv = b[key];
    // null/undefined は常に末尾
    const an = av == null || av === "";
    const bn = bv == null || bv === "";
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
    // 氏名は正規化名で比較すると姓のまとまりが揃いやすい
    if (key === "name") {
      return a.nameNormalized.localeCompare(b.nameNormalized, "ja") * mul;
    }
    return String(av).localeCompare(String(bv), "ja") * mul;
  };
}
